import {
  Add,
  Block,
  Close,
  DeleteOutline,
  EditOutlined,
  KeyOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  Chip,
  createSvgIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNotification } from "../../../../componentes/notificationProvider";
import api from "../../../../services/api";
import { ReactComponent as IconMaps } from "../../../../assets/icons/IconEasyMaps.svg";
import { ReactComponent as IconReport } from "../../../../assets/icons/IconEasyReport.svg";

const EasyMapsIcon = createSvgIcon(<IconMaps />);
const EasyReportIcon = createSvgIcon(<IconReport />);

// Add new modules here; the UI is a list and scales with it.
export const MODULOS = [
  {
    key: "easyMaps",
    label: "EasyMaps",
    description: "Mapas, topologia e georreferenciamento",
    Icon: EasyMapsIcon,
    color: { light: "#ec4b60", dark: "#e83750" }, // easyMapsTheme primary
  },
  {
    key: "easyReport",
    label: "EasyReport",
    description: "Relatórios e agendamentos",
    Icon: EasyReportIcon,
    color: { light: "#C05600", dark: "#ED7B00" }, // easyReportTheme primary
  },
];

const ACESSOS = [
  { value: "none", label: "Nenhum", Icon: Block },
  { value: "view", label: "Ver", Icon: VisibilityOutlined },
  { value: "edit", label: "Editar", Icon: EditOutlined },
];
export const ACESSO_LABEL = {
  none: "Sem acesso",
  view: "Somente visualizar",
  edit: "Pode editar",
};

const useStyles = (theme) => {
  return {
    header: { display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 2, pr: 1 },
    headerIcon: {
      display: "flex",
      p: 0.75,
      borderRadius: "8px",
      color: "primary.main",
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
    content: {
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      p: 0,
    },
    sidebar: {
      width: { xs: "100%", sm: 210 },
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      gap: 1,
      p: 1.25,
      backgroundColor: alpha(theme.palette.text.primary, 0.03),
      borderRight: { sm: `1px solid ${theme.palette.divider}` },
      borderBottom: { xs: `1px solid ${theme.palette.divider}`, sm: "none" },
    },
    sectionLabel: {
      fontSize: "11px",
      fontWeight: 600,
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
    },
    list: {
      p: 0,
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
      overflowY: "auto",
      maxHeight: { xs: 160, sm: 300 },
    },
    listItem: {
      borderRadius: "6px",
      py: 0.75,
      px: 1,
      gap: 1,
      borderLeft: "3px solid transparent",
      "&.Mui-selected": {
        borderLeftColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      },
    },
    dots: { display: "flex", gap: 0.5, ml: "auto", flexShrink: 0 },
    dot: (cor, nivel) => ({
      fontSize: "15px",
      color: nivel === "none" ? theme.palette.text.disabled : cor,
    }),
    newButton: {
      justifyContent: "flex-start",
      textTransform: "none",
      color: "primary.main",
      px: 1,
    },
    detail: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      p: 2,
      minWidth: 0,
    },
    moduleList: {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      overflowY: "auto",
      maxHeight: 320,
    },
    moduleRow: {
      display: "flex",
      flexWrap: { xs: "wrap", sm: "nowrap" },
      alignItems: "center",
      gap: 1.5,
      py: 1.25,
      px: 1.5,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "8px",
    },
    moduleIcon: (cor) => ({
      display: "flex",
      p: 0.75,
      borderRadius: "6px",
      color: cor,
      backgroundColor: alpha(cor, 0.12),
    }),
    segmented: {
      display: "flex",
      p: 0.375,
      gap: 0.25,
      borderRadius: "8px",
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      ml: { sm: "auto" },
      flexShrink: 0,
    },
    segment: (selected) => ({
      gap: 0.5,
      px: 1.25,
      py: 0.5,
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: 600,
      color: selected ? "primary.main" : "text.secondary",
      backgroundColor: selected ? theme.palette.background.paper : "transparent",
      boxShadow: selected ? `0 1px 3px ${alpha("#000", 0.15)}` : "none",
      transition: "all .15s",
      "&:hover": { color: selected ? "primary.main" : "text.primary" },
      "&.Mui-focusVisible": {
        outline: `2px solid ${theme.palette.primary.main}`,
      },
    }),
    actions: { px: 2, py: 1.25, gap: 1 },
  };
};

// Only "Usuário"-level roles live here; Super Admin / Admin are not editable.
export default function ModalUserRoles({ open, setOpen, roles, setRoles }) {
  const notify = useNotification();
  const theme = useTheme();
  const styles = useStyles(theme);
  const cor = (m) => m.color[theme.palette.mode];
  const [draft, setDraft] = useState(roles);
  const [selectedId, setSelectedId] = useState(roles[0]?.value);
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(roles);
    setSelectedId(roles[0]?.value);
    setIsSubmit(false);
  }, [open, roles]);

  const selected = draft.find((r) => r.value === selectedId);
  const changed = JSON.stringify(draft) !== JSON.stringify(roles);
  const nameError = (role) => {
    if (!role.name.trim()) return "Informe um nome";
    const dup = draft.some(
      (r) =>
        r.value !== role.value &&
        r.name.trim().toLowerCase() === role.name.trim().toLowerCase(),
    );
    return dup ? "Já existe um perfil com esse nome" : "";
  };

  const updateSelected = (changes) =>
    setDraft((d) =>
      d.map((r) => (r.value === selectedId ? { ...r, ...changes } : r)),
    );

  const setNivel = (key, nivel) =>
    updateSelected({ modulos: { ...selected.modulos, [key]: nivel } });

  const handleNew = () => {
    const role = {
      value: `user_${Date.now()}`,
      name: "",
      custom: true,
      modulos: Object.fromEntries(MODULOS.map((m) => [m.key, "none"])),
    };
    setDraft((d) => [...d, role]);
    setSelectedId(role.value);
  };

  const handleDelete = () => {
    const rest = draft.filter((r) => r.value !== selectedId);
    setDraft(rest);
    setSelectedId(rest[0]?.value);
  };

  const handleSave = async () => {
    setIsSubmit(true);
    const invalid = draft.find(nameError);
    if (invalid) {
      setSelectedId(invalid.value);
      return;
    }
    setLoading(true);
    try {
      const response = await api.put("/perfilPermissao/save", {
        roles: draft.map((r) => ({ ...r, name: r.name.trim() })),
      });
      setRoles(response.data);
      notify("Permissões salvas com sucesso!", "success");
      setOpen(false);
    } catch (error) {
      notify(
        error?.response?.data?.error ||
          "Não foi possível se conectar ao servidor!",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      PaperProps={{ sx: { borderRadius: "10px", maxWidth: 680 } }}
    >
      <DialogTitle sx={styles.header}>
        <Box sx={styles.headerIcon}>
          <KeyOutlined fontSize="small" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={600} fontSize="15px" lineHeight={1.3}>
            Permissões de usuário
          </Typography>
          <Typography fontSize="12px" color="text.secondary">
            Defina o acesso de cada perfil aos módulos
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Fechar" onClick={() => setOpen(false)}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={styles.content}>
        <Box sx={styles.sidebar}>
          <Box sx={{ display: "flex", alignItems: "center", px: 1, pt: 0.5 }}>
            <Typography sx={styles.sectionLabel}>Perfis</Typography>
            <Typography sx={{ ...styles.sectionLabel, ml: "auto" }}>
              {draft.length}
            </Typography>
          </Box>
          <List sx={styles.list}>
            {draft.map((r) => (
              <ListItemButton
                key={r.value}
                selected={r.value === selectedId}
                onClick={() => setSelectedId(r.value)}
                sx={styles.listItem}
              >
                <Typography
                  fontSize="13px"
                  fontWeight={r.value === selectedId ? 600 : 500}
                  noWrap
                  color={r.name ? "text.primary" : "text.secondary"}
                  fontStyle={r.name ? "normal" : "italic"}
                >
                  {r.name || "Novo perfil"}
                </Typography>
                <Box sx={styles.dots}>
                  {MODULOS.map((m) => {
                    const nivel = r.modulos[m.key] || "none";
                    return (
                      <Tooltip
                        key={m.key}
                        title={`${m.label}: ${ACESSO_LABEL[nivel]}`}
                      >
                        <m.Icon sx={styles.dot(cor(m), nivel)} />
                      </Tooltip>
                    );
                  })}
                </Box>
              </ListItemButton>
            ))}
          </List>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={handleNew}
            sx={styles.newButton}
          >
            Novo perfil
          </Button>
        </Box>

        {selected ? (
          <Box sx={styles.detail}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Typography sx={styles.sectionLabel}>Nome do perfil</Typography>
                {!selected.custom && (
                  <Chip
                    label="Padrão"
                    size="small"
                    sx={{ height: 18, fontSize: "11px" }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  key={selected.value}
                  size="small"
                  fullWidth
                  autoFocus={!selected.name}
                  placeholder="Ex.: Técnico de campo"
                  value={selected.name}
                  disabled={!selected.custom}
                  onChange={(e) => updateSelected({ name: e.target.value })}
                  error={isSubmit && !!nameError(selected)}
                  helperText={isSubmit && nameError(selected)}
                />
                {selected.custom && (
                  <Tooltip title="Excluir perfil">
                    <IconButton color="error" onClick={handleDelete}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box>
              <Typography sx={{ ...styles.sectionLabel, mb: 0.75 }}>
                Módulos
              </Typography>
              <Box sx={styles.moduleList}>
                {MODULOS.map((m) => {
                  const nivel = selected.modulos[m.key] || "none";
                  return (
                    <Box key={m.key} sx={styles.moduleRow}>
                      <Box sx={styles.moduleIcon(cor(m))}>
                        <m.Icon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography fontSize="13px" fontWeight={600} noWrap>
                          {m.label}
                        </Typography>
                        <Typography fontSize="12px" color="text.secondary" noWrap>
                          {m.description}
                        </Typography>
                      </Box>
                      <Box
                        role="radiogroup"
                        aria-label={`Acesso ao ${m.label}`}
                        sx={styles.segmented}
                      >
                        {ACESSOS.map((a) => {
                          const isSel = nivel === a.value;
                          return (
                            <ButtonBase
                              key={a.value}
                              role="radio"
                              aria-checked={isSel}
                              sx={styles.segment(isSel)}
                              onClick={() => setNivel(m.key, a.value)}
                            >
                              <a.Icon sx={{ fontSize: "15px" }} />
                              {a.label}
                            </ButtonBase>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ ...styles.detail, alignItems: "center", justifyContent: "center", minHeight: 200 }}>
            <Typography color="text.secondary" fontSize="14px">
              Selecione ou crie um perfil
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={styles.actions}>
        <Button onClick={() => setOpen(false)}>Cancelar</Button>
        <Button variant="contained" disabled={!changed || loading} onClick={handleSave}>
          Salvar alterações
        </Button>
      </DialogActions>
    </Dialog>
  );
}
